import { PersonLane } from '@app/modules/persons/appearances/types';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { usePersonLaneIdentity } from '../hooks/usePersonLaneIdentity';
import { PersonLaneAvatar } from '../shared/PersonLaneAvatar';

/** Matches PersonAvatar "large" for a clearer identity signal in the table. */
const VIEW_AVATAR_PX = 40;

type PersonsViewPersonCellProps = {
  lane: PersonLane;
  /** Whether the MRT detail panel for this row is open. */
  expanded: boolean;
  /** Toggles the row detail panel (replaces the default expand column). */
  onToggleExpand: () => void;
};

/**
 * "Person" column cell in the Personas results table.
 *
 * Shows expand control, lane-colored avatar ring (same border as the track),
 * resolved display name, and profession (or raw person id when unknown).
 */
export function PersonsViewPersonCell({
  lane,
  expanded,
  onToggleExpand,
}: PersonsViewPersonCellProps) {
  const { nameNode, profession } = usePersonLaneIdentity(lane);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      <IconButton size="small" onClick={onToggleExpand} sx={{ flexShrink: 0 }}>
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </IconButton>
      <PersonLaneAvatar lane={lane} sizePx={VIEW_AVATAR_PX} borderWidth={2} dashedWhenUnknown />
      <Box sx={{ minWidth: 0, pl: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
          {nameNode}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {lane.isUnknown ? lane.personId : (profession ?? '')}
        </Typography>
      </Box>
    </Stack>
  );
}
